import { useQuery } from "@tanstack/react-query";
import { getGameById, type Game as ApiGame } from "@/lib/apiSports";
import type { ScheduledGame, PeriodScores } from "@/hooks/useSchedule";

const REFRESH_INTERVAL_MS = 15_000; // 15 seconds

/**
 * Polls a single game by ID every 30 s while the game is live.
 * Returns an updated ScheduledGame (merged with the original) or null.
 */
export function useLiveGameRefresh(game: ScheduledGame | null, enabled: boolean) {
  const gameId = game?.id ?? 0;
  const isLive = game?.isLive ?? false;

  return useQuery({
    queryKey: ["liveGameRefresh", gameId],
    queryFn: async (): Promise<ScheduledGame | null> => {
      if (!gameId) return null;

      const response = await getGameById(gameId);

      if (!response?.response?.length) return null;

      const apiGame = response.response[0];
      return mergeApiUpdate(game!, apiGame);
    },
    enabled: enabled && isLive && gameId > 0,
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: 5_000,
    retry: 1,
  });
}

function parsePeriodScore(raw: string | null | undefined): { home: number | null; away: number | null } {
  if (!raw) return { home: null, away: null };
  const parts = raw.split("-");
  return {
    home: parts[0] !== undefined ? Number(parts[0]) : null,
    away: parts[1] !== undefined ? Number(parts[1]) : null,
  };
}

function mergeApiUpdate(original: ScheduledGame, api: ApiGame): ScheduledGame {
  const statusShort = api.status.short.toUpperCase();
  const liveStatuses = ["1P", "2P", "3P", "P1", "P2", "P3", "OT", "BT", "P", "LIVE", "HT", "INT"];
  const finishedStatuses = ["FT", "AOT", "AP", "AET", "POST"];
  const isLive = liveStatuses.includes(statusShort);
  const isFinished = finishedStatuses.includes(statusShort);

  const periods: PeriodScores = api.periods
    ? {
        first: typeof api.periods.first === "string"
          ? parsePeriodScore(api.periods.first as unknown as string)
          : api.periods.first ?? { home: null, away: null },
        second: typeof api.periods.second === "string"
          ? parsePeriodScore(api.periods.second as unknown as string)
          : api.periods.second ?? { home: null, away: null },
        third: typeof api.periods.third === "string"
          ? parsePeriodScore(api.periods.third as unknown as string)
          : api.periods.third ?? { home: null, away: null },
        overtime: typeof api.periods.overtime === "string"
          ? parsePeriodScore(api.periods.overtime as unknown as string)
          : api.periods.overtime ?? { home: null, away: null },
        penalties: typeof api.periods.penalties === "string"
          ? parsePeriodScore(api.periods.penalties as unknown as string)
          : api.periods.penalties ?? { home: null, away: null },
      }
    : original.periods;

  return {
    ...original,
    homeScore: api.scores.home,
    awayScore: api.scores.away,
    status: api.status.long,
    statusShort: api.status.short,
    isLive,
    isFinished,
    isScheduled: !isLive && !isFinished,
    periods,
  };
}
